<template>
  <div
class="w-full transition-all duration-200 fade-in"
  :class="{ 'opacity-50': loading }">
    <template v-if="hasApplicants">
      <table class="w-full">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-200">
            <th
              class="p-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-10"
            ></th>
            <th class="p-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Applicant
            </th>
            <th
              v-if="isGuide"
              class="p-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
            >
              Program Status
            </th>
            <th class="p-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Batch
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          <tr
            v-for="applicant in applicants"
            :key="applicant.user_details.id"
            class="hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
            @click="handleRowClick(applicant.user_details.id)"
          >
            <!-- Avatar -->
            <td class="p-3">
              <div
                v-if="applicant.user_details.avatar_image"
                class="h-10 min-w-10 aspect-square rounded-full overflow-hidden ring-1 ring-gray-200"
              >
                <img
                  :src="applicant.user_details.avatar_image"
                  :alt="`${applicant.user_details.first_name} ${applicant.user_details.last_name}`"
                  class="h-10 min-w-10 aspect-square object-cover"
                  :class="{ 'privacy-blur': isBlurred }"
                />
              </div>
              <div
                v-else
                class="h-10 min-w-10 aspect-square rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm"
              >
                <span :class="{ 'privacy-blur': isBlurred }">
                  {{
                    getInitials(applicant.user_details.first_name, applicant.user_details.last_name)
                  }}
                </span>
              </div>
            </td>

            <!-- Applicant Name and Email -->
            <td class="p-1">
              <div class="font-medium text-sm" :class="{ 'privacy-blur': isBlurred }">
                <template v-if="props.ordering === 'last_name'">
                  {{ applicant.user_details.last_name }} {{ applicant.user_details.first_name }}
                </template>
                <template v-else>
                  {{ applicant.user_details.first_name }} {{ applicant.user_details.last_name }}
                </template>
              </div>
              <div class="text-sm text-gray-600" :class="{ 'privacy-blur': isBlurred }">
                {{ applicant.user_details.email }}
              </div>
            </td>

            <!-- Program Status -->
            <td v-if="isGuide" class="p-3">
              <span
                :class="[
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs',
                  getProgramStatusStyles(applicant.fellow_data.outtalent_program_status).bgColor,
                  getProgramStatusStyles(applicant.fellow_data.outtalent_program_status).textColor
                ]"
              >
                {{ getProgramStatusLabel(applicant.fellow_data.outtalent_program_status) }}
              </span>
            </td>

            <!-- Batch -->
            <td class="p-3 text-sm text-gray-600">
              {{ getBatchName(applicant.fellow_data.batch) }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <div v-else class="flex flex-col items-center justify-center p-8 text-gray-500 gap-2 text-sm fade-in">
      <HeartCrack class="w-4 h-4" />
      No matching applicants
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import type { Applicant } from '@/types/applicants'
import { getProgramStatusLabel, getProgramStatusStyles } from '@/const/fellowStatuses'
import { useAuth } from '@/composables/useAuth'
import { usePrivacy } from '@/composables/usePrivacy'
import { HeartCrack } from 'lucide-vue-next'

const { isGuide } = useAuth()
const { isBlurred } = usePrivacy()

interface Props {
  applicants: Applicant[]
  loading: boolean
  hasApplicants: boolean
  getBatchName: (batchId: string) => string
  ordering?: string
}

const props = defineProps<Props>()

const router = useRouter()

const handleRowClick = (applicantId: number) => {
  if (isBlurred.value) {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push(`/applicant/${applicantId}`)
}

const getInitials = (firstName: string, lastName: string) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
}
</script>

<style scoped></style>
